<template>
  <div class="list-page-container">
    <!-- 顶部操作区：副标题与创建按钮 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-user"></i>
          <span>{{ $t('userManagement.userListTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleCreateUser" v-if="hasPermission('sys:user:save')">
            <i class="el-icon-plus"></i> {{ $t('userManagement.createUser') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 统一控制中心：筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <!-- 搜索筛选区 -->
        <el-form :inline="true" :model="searchForm" class="filter-form">
          <el-form-item>
            <el-input
              v-model="searchForm.username"
              :placeholder="$t('userManagement.searchUsername')"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-user"></i>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-input
              v-model="searchForm.mobile"
              :placeholder="$t('userManagement.searchMobile')"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-mobile-phone"></i>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-select
              v-model="searchForm.status"
              :placeholder="$t('userManagement.filterByStatus')"
              clearable
              class="filter-select"
              @change="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-set-up"></i>
              <el-option :label="$t('common.inUse')" :value="1"></el-option>
              <el-option :label="$t('common.frozen')" :value="2"></el-option>
              <el-option :label="$t('common.cancelled')" :value="0"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-select
              v-model="searchForm.userLevel"
              placeholder="用户等级"
              clearable
              class="filter-select"
              @change="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-medal"></i>
              <el-option
                v-for="item in levelFilterOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-select
              v-model="searchForm.group_id"
              :placeholder="$t('userManagement.filterByGroup')"
              clearable
              filterable
              class="filter-select"
              @change="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-folder"></i>
              <el-option
                v-for="group in userGroups"
                :key="group.id"
                :label="group.name"
                :value="group.id"
              ></el-option>
            </el-select>
          </el-form-item>
          <div class="search-btns">
            <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('common.search') }}</el-button>
            <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
          </div>
        </el-form>

        <!-- 批量操作与工具区 -->
        <div class="action-wrapper">
          <div class="left-actions">
            <el-button-group class="batch-group">
              <el-button 
                type="warning" 
                @click="handleBatchAssignGroup" 
                :disabled="!selectedUsers.length"
                plain
                size="small"
                v-if="hasPermission('sys:usergroup:update')"
              >
                <i class="el-icon-folder-add"></i> {{ $t('userManagement.addUsersToGroup') }}
              </el-button>
              <el-button 
                type="warning" 
                @click="handleBatchGroupOperation" 
                :disabled="!selectedGroupId"
                plain
                size="small"
                v-if="hasPermission('sys:usergroup:update')"
              >
                <i class="el-icon-s-cooperation"></i> {{ $t('userManagement.batchGroupOperation') }}
              </el-button>
            </el-button-group>
          </div>
          <div class="right-tools">
            <el-button @click="fetchData" icon="el-icon-refresh" circle size="small" :title="$t('common.refresh')"></el-button>
            <el-button type="info" @click="handleExport" :loading="exportLoading" icon="el-icon-download" circle size="small" :title="$t('common.export')" v-if="hasPermission('sys:user:export')"></el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 用户列表 -->
    <el-card class="list-card">
      <el-table
        :data="allUsers"
        v-loading="tableLoading"
        height="100%"
        style="width: 100%"
        @selection-change="handleSelectionChange"
        :element-loading-text="$t('common.loading')"
        :row-class-name="getUserRowClassName"
        class="custom-table"
      >
        <el-table-column type="selection" width="55" align="center"></el-table-column>
        <el-table-column :label="$t('userManagement.username')" min-width="150" show-overflow-tooltip align="center">
          <template slot-scope="scope">
            <span class="username-text">{{ scope.row.username }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('userManagement.name')" min-width="120" show-overflow-tooltip align="center"></el-table-column>
        <el-table-column prop="mobile" :label="$t('userManagement.phone')" width="120" align="center"></el-table-column>
        <el-table-column prop="userLevel" :label="$t('userManagement.userLevel')" width="140" align="center">
          <template slot-scope="scope">
            <el-tag
              size="small"
              :color="getLevelColor(scope.row.userLevel)"
              effect="dark"
              style="color: #fff; border: none;"
            >
              {{ getLevelDisplayText(scope.row.userLevel) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="accountBalance" :label="$t('userManagement.accountBalance')" width="120" align="center">
          <template slot-scope="scope">
            <span class="balance-text">¥ {{ formatBalance(scope.row.accountBalance) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="points" :label="$t('userManagement.points')" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="small" effect="plain" type="warning">{{ scope.row.points || 0 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="role" :label="$t('userManagement.role')" min-width="120" align="center">
          <template slot-scope="scope">
            <div class="role-tags">
              <el-tag 
                v-if="getRoleName(scope.row)"
                size="small" 
                :type="getRoleTagType(scope.row)" 
                effect="plain"
                class="role-tag-item"
              >
                {{ getRoleName(scope.row) }}
              </el-tag>
              <span v-else class="text-secondary">-</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="status" :label="$t('common.status')" width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="getStatusTagType(scope.row.status)"
              size="small"
              effect="dark"
            >
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createDate" :label="$t('common.createdAt')" width="170" align="center">
          <template slot-scope="scope">
            <span class="time-text">{{ formatDate(scope.row.createDate) }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.actions')" :width="actionColumnWidth" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button
                type="primary"
                size="mini"
                plain
                @click="handleViewDetail(scope.row.userid)"
                icon="el-icon-view"
                :aria-label="$t('common.detail')"
                v-if="canViewUser"
              >
                {{ $t('common.detail') }}
              </el-button>
              <el-button
                type="warning"
                size="mini"
                plain
                @click="handleEditUser(scope.row.userid)"
                icon="el-icon-edit"
                :disabled="scope.row.username === 'admin'"
                :aria-label="$t('common.edit')"
                v-if="canEditUser && scope.row.status !== 0"
              >
                {{ $t('common.edit') }}
              </el-button>
              <el-button
                v-if="canEditUser && scope.row.status !== 0"
                :type="scope.row.status === 2 ? 'success' : 'info'"
                size="mini"
                plain
                @click="handleFreezeUser(scope.row)"
                :icon="scope.row.status === 2 ? 'el-icon-unlock' : 'el-icon-lock'"
                :aria-label="scope.row.status === 2 ? $t('common.unfreeze') : $t('common.freeze')"
              >
                {{ scope.row.status === 2 ? $t('common.unfreeze') : $t('common.freeze') }}
              </el-button>
              <el-button
                type="danger"
                size="mini"
                plain
                @click="handleCancelUser(scope.row.userid)"
                icon="el-icon-switch-button"
                :disabled="scope.row.username === 'admin'"
                :aria-label="$t('common.cancelAccount')"
                v-if="canDeleteUser && scope.row.status !== 0"
              >
                {{ $t('common.cancelAccount') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 底部悬浮批量操作栏 -->
    <transition name="el-zoom-in-bottom">
      <div v-if="selectedUsers.length > 0" class="batch-footer-bar">
        <div class="batch-info">
          <i class="el-icon-info"></i>
          <span>{{ $t('userManagement.selectedCount', { count: selectedUsers.length }) }}</span>
        </div>
        <div class="batch-buttons">
          <el-button type="danger" size="small" icon="el-icon-switch-button" @click="handleBatchCancelAccount" v-if="hasPermission('sys:user:delete')">{{ $t('common.cancelAccount') }}</el-button>
          <el-button type="info" size="small" icon="el-icon-lock" @click="handleBatchFreeze" v-if="hasPermission('sys:user:update')">{{ $t('common.freeze') }}</el-button>
          <el-button type="success" size="small" icon="el-icon-unlock" @click="handleBatchUnfreeze" v-if="hasPermission('sys:user:update')">{{ $t('common.unfreeze') }}</el-button>
          <el-divider direction="vertical" v-if="hasPermission('sys:user:delete') || hasPermission('sys:user:update')"></el-divider>
          <el-button type="text" @click="selectedUsers = []">{{ $t('common.cancel') }}</el-button>
        </div>
      </div>
    </transition>
    
    <!-- 分组批量操作对话框 -->
    <el-dialog
      :title="$t('userManagement.groupBatchOperation')"
      :visible.sync="groupOperationDialog.visible"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="group-operation-content">
        <p>{{ $t('userManagement.groupBatchOperationDesc', { groupName: groupOperationDialog.groupName, count: groupOperationDialog.users.length }) }}</p>
        
        <el-table :data="groupOperationDialog.users" style="width: 100%; margin-top: 20px;" size="small">
          <el-table-column prop="username" :label="$t('userManagement.username')"></el-table-column>
          <el-table-column prop="name" :label="$t('userManagement.name')"></el-table-column>
          <el-table-column prop="status" :label="$t('common.status')">
            <template slot-scope="scope">
              <el-tag :type="getStatusTagType(scope.row.status)" size="small" effect="dark">
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="groupOperationDialog.visible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="danger" @click="executeGroupBatchOperation('delete')" v-if="hasPermission('sys:user:delete')">
          {{ $t('userManagement.deleteGroupUsers') }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 分配到分组对话框 -->
    <el-dialog
      :title="$t('userManagement.addUsersToGroup')"
      :visible.sync="assignGroupDialog.visible"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="assign-group-content">
        <p style="margin-bottom: 20px;">{{ $t('userManagement.assignUsersToGroupDesc', { count: selectedUsers.length }) }}</p>
        
        <el-form :model="assignGroupForm" label-width="100px">
          <el-form-item :label="$t('userManagement.userGroups')">
            <el-select v-model="assignGroupForm.groupId" :placeholder="$t('userManagement.selectUserGroup')" filterable style="width: 100%;">
              <el-option
                v-for="group in userGroups"
                :key="group.id"
                :label="group.name"
                :value="group.id"
              ></el-option>
            </el-select>
          </el-form-item>
          
          <el-form-item :label="$t('userManagement.operationType')">
            <el-radio-group v-model="assignGroupForm.operation">
              <el-radio label="add">{{ $t('userManagement.addUsers') }}</el-radio>
              <el-radio label="remove">{{ $t('userManagement.removeFromGroup') }}</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="assignGroupDialog.visible = false">{{ $t('common.cancel') }}</el-button>
        <el-button 
          type="primary" 
          @click="executeAssignGroup" 
          :loading="assignGroupDialog.loading"
          :disabled="!assignGroupForm.groupId"
          v-if="hasPermission('sys:usergroup:update')"
        >
          {{ $t('common.confirm') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { hasPermission } from '@/utils/permission'
import { getFullUrl } from '@/utils/index'
import { getLevelOptions } from '@/apis/system/level'

export default {
  name: 'UserList',
  data() {
    return {
      loading: false,
      tableLoading: false,
      exportLoading: false,
      levelOptions: [],
      searchForm: {
        username: '',
        mobile: '',
        group_id: '',
        status: '',
        userLevel: ''
      },
      page: 1,
      limit: 10,
      total: 0,
      selectedUsers: [],
      groupOperationDialog: {
        visible: false,
        groupId: null,
        groupName: '',
        users: []
      },
      assignGroupDialog: {
        visible: false,
        loading: false
      },
      assignGroupForm: {
        groupId: '',
        operation: 'add'
      }
    }
  },
  computed: {
    allUsers() {
      return this.$store.getters.allUsers
    },
    userGroups() {
      return this.$store.getters.allUserGroups
    },
    allRoles() {
      return this.$store.getters.allRoles
    },
    selectedGroupId() {
      return this.searchForm.group_id
    },
    levelFilterOptions() {
      return (this.levelOptions || []).map(item => ({
        label: item.levelCode + ' ' + item.levelName,
        value: item.levelCode
      }))
    },
    // 操作列权限判断
    canViewUser() {
      return this.hasPermission('sys:user:info')
    },
    canEditUser() {
      return this.hasPermission('sys:user:update')
    },
    canDeleteUser() {
      return this.hasPermission('sys:user:delete')
    },
    // 操作列可见按钮数量（基于权限，不含行级条件）
    visibleActionCount() {
      let count = 0
      if (this.canViewUser) count += 1   // 查看按钮
      if (this.canEditUser) count += 2    // 编辑 + 冻结/解冻按钮
      if (this.canDeleteUser) count += 1  // 注销按钮
      return count
    },
    // 是否有任何操作权限
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    // 操作列动态宽度
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170, 3: 245, 4: 300 }
      return widthMap[count] || 0
    }
  },
  async created() {
    this.loading = true
    try {
      await this.fetchData()
      await this.fetchGroups()

      // 获取角色列表
      if (this.allRoles.length === 0) {
        this.$store.dispatch('fetchRoles')
      }

      // 获取等级选项
      getLevelOptions().then(res => {
        this.levelOptions = res || []
      }).catch(() => {})
    } finally {
      this.loading = false
    }
  },
  methods: {
    hasPermission,
    getUserRowClassName({ row }) {
      if (row.status === 0) return 'cancelled-user-row'
      if (row.status === 2) return 'frozen-user-row'
      return ''
    },
    getStatusTagType(status) {
      if (status === 0) return 'info'
      if (status === 2) return 'warning'
      return 'success'
    },
    getStatusText(status) {
      if (status === 0) return this.$t('common.cancelled')
      if (status === 2) return this.$t('common.frozen')
      return this.$t('common.inUse')
    },
    // 获取用户角色名称（仅显示一个）
    getRoleName(user) {
      if (!user) return ''
      
      // 优先从 roleId 获取角色
      const roleId = user.roleId
      if (roleId) {
        const role = this.allRoles.find(r => String(r.id) === String(roleId))
        if (role) return role.roleName
      }
      
      // 回退到基础 role 字段
      if (user.role === 'admin') return this.$t('userManagement.admin')
      if (user.role === 'user') return this.$t('userManagement.user')
      
      return ''
    },
    // 统一获取角色标签颜色
    getRoleTagType(user) {
      if (!user) return 'info'
      
      // 1. 优先检查是否是超级管理员
      if (user.superAdmin === 1) return 'danger'
      
      // 2. 检查角色的类型
      const roleId = user.roleId
      if (roleId) {
        const role = this.allRoles.find(r => String(r.id) === String(roleId))
        if (role) {
          if (role.roleCode === 'superAdmin') return 'danger'
          if (role.roleCode === 'admin') return 'warning'
        }
      }
      
      // 3. 兜底逻辑
      if (user.role === 'admin') return 'warning'
      return 'success'
    },
    // 获取用户等级标签类型
    getLevelColor(level) {
      const item = this.levelOptions.find(l => l.levelCode === level)
      return item && item.levelColor ? item.levelColor : '#909399'
    },
    getLevelDisplayText(level) {
      const item = this.levelOptions.find(l => l.levelCode === level)
      return item ? item.levelCode + ' ' + item.levelName : (level || 'V0')
    },
    // 格式化余额
    formatBalance(balance) {
      if (balance === undefined || balance === null) return '0.00'
      return parseFloat(balance).toFixed(2)
    },
    async fetchGroups() {
      try {
        this.loading = true
        await this.$store.dispatch('fetchUserGroups')
      } catch (error) {
        this.$message.error(this.$t('userManagement.fetchGroupError'))
      } finally {
        this.loading = false
      }
    },
    async fetchData() {
      try {
        this.tableLoading = true
        const params = {
          page: this.page,
          limit: this.limit,
          username: this.searchForm.username || undefined,
          mobile: this.searchForm.mobile || undefined,
          userLevel: this.searchForm.userLevel || undefined,
          status: this.searchForm.status !== '' ? this.searchForm.status : undefined,
          groupId: this.searchForm.group_id || undefined
        }

        await this.$store.dispatch('fetchUsers', params)
        this.total = this.$store.state.totalUsers || 0
      } catch (error) {
        this.$message.error(this.$t('userManagement.fetchErrorDetailed'))
      } finally {
        this.tableLoading = false
      }
    },
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    
    // 创建用户
    handleCreateUser() {
      this.$router.push('/user/create')
    },
    
    // 查看用户详情
    handleViewDetail(id) {
      this.$router.push(`/user/detail/${id}`)
    },
    
    // 编辑用户
    handleEditUser(id) {
      this.$router.push(`/user/edit/${id}`)
    },
    
    // 删除用户
    handleCancelUser(id) {
      const user = this.$store.getters.userById(id) || this.allUsers.find(u => String(u.userid) === String(id))
      if (!user) {
        this.$message.error('未找到用户信息')
        return
      }
      this.$confirm(
        this.$t('common.confirmCancelAccount'),
        this.$t('common.confirm'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(() => {
        this.$store.dispatch('deleteUser', id).then(() => {
          this.$message.success(this.$t('common.cancelAccountSuccess'))
        }).catch(err => {
          this.$message.error('注销用户失败')
        })
      }).catch(() => {
        this.$message.info(this.$t('common.cancel'))
      })
    },

    handleFreezeUser(user) {
      const isFrozen = user.status === 2
      const actionText = isFrozen ? this.$t('common.unfreeze') : this.$t('common.freeze')
      const confirmMsg = isFrozen ? this.$t('common.confirmUnfreeze') : this.$t('common.confirmFreeze')

      this.$confirm(confirmMsg, this.$t('common.confirm'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        try {
          await import('@/apis/user/user.js').then(m => m.updateUserStatus(user.userid, isFrozen ? 1 : 2))
          user.status = isFrozen ? 1 : 2
          this.$message.success(isFrozen ? this.$t('common.unfreezeSuccess') : this.$t('common.freezeSuccess'))
        } catch (err) {
          this.$message.error((isFrozen ? '解冻' : '冻结') + '操作失败')
        }
      }).catch(() => {
        this.$message.info(this.$t('common.cancel'))
      })
    },

    handleBatchFreeze() {
      const validUsers = this.selectedUsers.filter(u => u.status !== 0 && u.username !== 'admin' && u.status !== 2)
      if (validUsers.length === 0) {
        this.$message.warning('没有可冻结的用户（已注销/已冻结/admin用户除外）')
        return
      }
      this.$confirm(
        this.$t('common.confirmBatchFreeze') + ` (${validUsers.length}人)`,
        this.$t('common.confirm'),
        { type: 'warning' }
      ).then(async () => {
        try {
          const ids = validUsers.map(u => u.userid)
          await import('@/apis/user/user.js').then(m => m.batchUpdateUserStatus(ids, 2))
          validUsers.forEach(u => { u.status = 2 })
          this.$message.success(this.$t('common.batchFreezeSuccess'))
          this.selectedUsers = []
        } catch (err) {
          this.$message.error('批量冻结失败')
        }
      }).catch(() => {})
    },

    handleBatchUnfreeze() {
      const frozenUsers = this.selectedUsers.filter(u => u.status === 2)
      if (frozenUsers.length === 0) {
        this.$message.warning('没有可解冻的用户')
        return
      }
      this.$confirm(
        this.$t('common.confirmBatchUnfreeze') + ` (${frozenUsers.length}人)`,
        this.$t('common.confirm'),
        { type: 'warning' }
      ).then(async () => {
        try {
          const ids = frozenUsers.map(u => u.userid)
          await import('@/apis/user/user.js').then(m => m.batchUpdateUserStatus(ids, 1))
          frozenUsers.forEach(u => { u.status = 1 })
          this.$message.success(this.$t('common.batchUnfreezeSuccess'))
          this.selectedUsers = []
        } catch (err) {
          this.$message.error('批量解冻失败')
        }
      }).catch(() => {})
    },

    handleBatchCancelAccount() {
      const validUsers = this.selectedUsers.filter(u => u.status !== 0 && u.username !== 'admin')
      if (validUsers.length === 0) {
        this.$message.warning('没有可注销的用户（已注销/admin用户除外）')
        return
      }
      this.$confirm(
        this.$t('common.confirmBatchCancelAccount') + ` (${validUsers.length}人)`,
        this.$t('common.confirm'),
        { type: 'warning' }
      ).then(async () => {
        try {
          const ids = validUsers.map(u => u.userid)
          await this.$store.dispatch('batchDeleteUsers', ids)
          this.$message.success(this.$t('common.batchCancelAccountSuccess'))
          this.selectedUsers = []
        } catch (err) {
          this.$message.error('批量注销失败')
        }
      }).catch(() => {})
    },
    
    // 搜索用户
    handleSearch() {
      this.page = 1
      this.fetchData()
    },

    // 导出用户列表
    handleExport() {
      this.$confirm(this.$t('userManagement.confirmExportUsers') || '确定要导出用户列表吗？', this.$t('common.prompt'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'info'
      }).then(() => {
        this.exportLoading = true
        
        try {
          // 模拟导出逻辑：构建 CSV 内容
          // 实际项目中应调用 API 获取 Blob 数据
          const headers = [
            this.$t('userManagement.username'),
            this.$t('userManagement.name') || '姓名',
            this.$t('userManagement.userLevel') || '等级',
            this.$t('userManagement.accountBalance') || '余额',
            this.$t('userManagement.points') || '积分',
            this.$t('userManagement.email'),
            this.$t('userManagement.mobile'),
            this.$t('userManagement.status'),
            this.$t('common.createdAt')
          ].join(',')

          const rows = this.allUsers.map(user => {
            return [
              user.username,
              user.name || '-',
              user.userLevel || 'V0',
              user.accountBalance || 0,
              user.points || 0,
              user.email || '-',
              user.mobile || '-',
              user.status === 1 ? this.$t('common.inUse') : user.status === 2 ? this.$t('common.frozen') : this.$t('common.cancelled'),
              this.formatDate(user.createdAt)
            ].join(',')
          })

          const csvContent = "\ufeff" + [headers, ...rows].join('\n')
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
          const link = document.createElement("a")
          const url = URL.createObjectURL(blob)
          
          link.setAttribute("href", url)
          link.setAttribute("download", `users_export_${new Date().getTime()}.csv`)
          link.style.visibility = 'hidden'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          this.$message.success(this.$t('common.exportSuccess'))
        } catch (error) {
          this.$message.error(this.$t('common.exportFailed'))
        } finally {
          this.exportLoading = false
        }
      }).catch(() => {
        // 取消导出
      })
    },
    
    // 重置搜索
    handleReset() {
      this.searchForm = {
        username: '',
        mobile: '',
        status: '',
        userLevel: '',
        group_id: ''
      }
      this.page = 1
      this.fetchData()
    },
    
    // 处理用户状态变更
    handleStatusChange(row, newVal) {
      if (row.status === 0) {
        this.$message.warning(this.$t('userManagement.cannotChangeCancelledStatus'))
        return
      }
    },
    
    // 分页处理
    handleSizeChange(size) {
      this.limit = size
      this.page = 1
      this.fetchData()
    },

    handleCurrentChange(page) {
      this.page = page
      this.fetchData()
    },
    
    // 批量选择
    handleSelectionChange(val) {
      this.selectedUsers = val
    },
    
    // 批量注销
    handleBatchDelete() {
      const userIds = this.selectedUsers.map(user => user.userid)
      const hasAdmin = this.selectedUsers.some(user => user.username === 'admin')
      
      if (hasAdmin) {
        this.$message.warning(this.$t('userManagement.cannotDeleteAdmin'))
        return
      }
      
      this.$confirm(
        this.$t('common.confirmBatchCancelAccount') + ` (${userIds.length}人)`,
        this.$t('common.confirm'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(() => {
        this.$store.dispatch('batchDeleteUsers', userIds).then(() => {
          this.$message.success(this.$t('common.batchCancelAccountSuccess'))
          this.selectedUsers = []
        }).catch(err => {
          this.$message.error('批量注销失败')
        })
      }).catch(() => {
        this.$message.info(this.$t('common.cancel'))
      })
    },

    // 批量分配到分组
    handleBatchAssignGroup() {
      if (this.selectedUsers.length === 0) {
        this.$message.warning(this.$t('userManagement.selectUserFirst'))
        return
      }
      this.assignGroupForm.groupId = ''
      this.assignGroupForm.operation = 'add'
      this.assignGroupDialog.visible = true
    },

    // 执行分配到分组
    async executeAssignGroup() {
      const { groupId, operation } = this.assignGroupForm
      const selectedUserIds = this.selectedUsers.map(user => user.userid)
      
      console.log('Execute Assign Group:', { groupId, operation, selectedUserIds })
      
      this.assignGroupDialog.loading = true
      try {
        // 获取当前分组的完整信息，以获取现有的用户ID列表
        const group = this.userGroups.find(g => String(g.id) === String(groupId))
        if (!group) {
          throw new Error(this.$t('userManagement.groupNotFound'))
        }

        // 这里的 userIds 应该是 DTO 中的 field name，根据后端 DTO，它是 user_ids (via JsonProperty)
        // 但在 store 中 mapped 之后可能是 userIds
        const currentUserIds = group.userIds || group.user_ids || []
        let newUserIds = []

        if (operation === 'add') {
          // 合并并去重
          newUserIds = [...new Set([...currentUserIds, ...selectedUserIds])]
          console.log('Adding users to group. New user IDs:', newUserIds)
        } else {
          // 过滤掉选中的用户
          newUserIds = currentUserIds.filter(id => !selectedUserIds.includes(String(id)) && !selectedUserIds.includes(Number(id)))
          console.log('Removing users from group. New user IDs:', newUserIds)
        }

        // 调用 store 的 updateUserGroup action，它最终调用 PUT /user/groups
        await this.$store.dispatch('updateUserGroup', {
          id: groupId,
          user_ids: newUserIds
        })

        this.$message.success(operation === 'add' 
          ? this.$t('userManagement.addUsersSuccess') 
          : this.$t('userManagement.removeUserSuccess'))
        
        this.assignGroupDialog.visible = false
        this.selectedUsers = []
        
        // 重新获取数据以更新列表显示
        await this.fetchData()
        // 同时更新分组信息，确保下次操作时 userIds 是最新的
        await this.$store.dispatch('fetchUserGroups')
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.message || 'Unknown error'
        this.$message.error(`${this.$t('userManagement.addUsersError')}: ${errorMsg}`)
      } finally {
        this.assignGroupDialog.loading = false
      }
    },
    
    // 分组批量操作
    handleBatchGroupOperation() {
      if (!this.selectedGroupId) {
        this.$message.warning(this.$t('userManagement.pleaseSelectGroup'))
        return
      }
      
      const group = this.$store.getters.userGroupById(this.selectedGroupId)
      if (!group) {
        this.$message.error(this.$t('userManagement.groupNotFound'))
        return
      }
      
      // 获取分组下的所有用户 (当前页)
      // 兼容 groupIds 和 groupIdList 字段
      const groupUsers = this.allUsers.filter(user => {
        const ids = user.groupIds || user.groupIdList || []
        return ids.some(id => String(id) === String(this.selectedGroupId))
      })
      
      console.log('Batch Group Operation:', {
        selectedGroupId: this.selectedGroupId,
        foundGroupUsers: groupUsers.length,
        allUsersCount: this.allUsers.length
      })
      
      if (groupUsers.length === 0) {
        this.$message.warning(this.$t('userManagement.groupHasNoUsers'))
        return
      }
      
      this.groupOperationDialog = {
        visible: true,
        groupId: this.selectedGroupId,
        groupName: group.name,
        users: groupUsers
      }
    },
    
    // 执行分组批量操作
    executeGroupBatchOperation(operation) {
      const { groupId, users } = this.groupOperationDialog
      
      let targetUsers = [...users]
      targetUsers = targetUsers.filter(user => user.username !== 'admin')
      targetUsers = targetUsers.filter(user => user.status !== 0)
      
      if (targetUsers.length === 0) {
        this.$message.warning(this.$t('userManagement.noValidUsers'))
        return
      }
      
      const userIds = targetUsers.map(user => user.userid)
      
      if (operation === 'delete') {
        this.batchDeleteByIds(userIds)
      }
      
      this.groupOperationDialog.visible = false
    },
    
    // 根据ID批量删除
    batchDeleteByIds(userIds) {
      this.$confirm(
        this.$t('userManagement.batchDeleteGroupUsersConfirm', { count: userIds.length }),
        this.$t('common.confirm'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(async () => {
        try {
          await this.$store.dispatch('batchDeleteUsers', userIds)
          this.$message.success(this.$t('userManagement.batchDeleteGroupUsersSuccess'))
          this.selectedUsers = []
          this.fetchData()
        } catch (err) {
          this.$message.error(this.$t('userManagement.batchDeleteGroupUsersError'))
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 用户列表页面特有样式 - 通用样式已在 list-page.scss 中定义 */

.group-operation-content p {
  color: #606266;
  line-height: 1.6;
}
</style>